/**
 * The Nudge sheet — the drafted message, and nothing else.
 *
 * ## There is no send button, and that is the design
 *
 * Spec §3.6: v1 treats every partner as external. There is no in-app channel,
 * no sub account, no presence. So a nudge is a DRAFT the user copies and
 * sends from their own mail or SMS client, and this sheet's primary action is
 * **Copy**.
 *
 * That is not a shortcut around an unimplemented transport. `prepareNudge` is
 * a pure string composer with one export and no I/O — no database read, no
 * network call, not even a clock read — and `webapp/` owns no writes at all
 * (spec §5.3). Just as deliberately, it does NOT emit the `nudged` item
 * event: `nudged` counts as MOVEMENT, so firing it when a user merely opens
 * this sheet would reset the stall clock for a message nobody sent, and the
 * Brief would then report an item as freshly chased when it was not. The
 * event belongs to `backend/`, when the user actually acts.
 *
 * The source design draws a channel-routed primary (`Send in LedAI` /
 * `Open in email` / `Open in messages`) chosen from a contacts table that
 * marks some companies as LedAI users. That table does not exist in v1 — no
 * partner is on LedAI, and no record anywhere holds a phone number — so this
 * sheet offers BOTH drafts and lets the user pick, rather than silently
 * guessing a channel from a stored assignee string that is free text.
 *
 * ## The recipient is reported, not implied
 *
 * `prepareNudge` sets `email.to` only when the stored assignee really parses
 * as an address, and `needsRecipientLookup` says so otherwise. The sheet
 * prints that rather than rendering an unsendable draft that looks ready: "we
 * do not have an address for Sam Chu" is the fact the user needs before they
 * paste.
 *
 * ## This is not the ball affordance (spec §4)
 *
 * Ball-in-court is gone from every list, row and card in the console. This
 * sheet still names WHO the draft is addressed to, because that is the one
 * question a drafted message cannot be honest without answering — and it
 * comes labelled with how it was derived, rather than presented as a field of
 * the record. What went is the vocabulary and the pills, not the ability to
 * say who you are writing to.
 */

import { copyToClipboard } from "../lib/clipboard";
import { useEffect, useState } from "react";
import { fetchNudge, type ApiError, type NudgeChannel, type NudgeResponse } from "../api";
import { useResource } from "../useResource";
import { C, FONT_MONO } from "../tokens";
import {
  FailurePanel,
  KindChip,
  META,
  Pill,
  ROW_LINE,
  ROW_TITLE,
  STATUS_CHIP,
} from "./chrome";

/** What each channel is called, and what the sheet says about how it will
 *  actually leave the building. */
const CHANNELS: Array<{ id: NudgeChannel; tab: string; label: string }> = [
  { id: "email", tab: "Email", label: "Drafted email" },
  { id: "text", tab: "Text", label: "Drafted text" },
];

export function NudgeSheet({
  itemId,
  search,
  onClose,
}: {
  itemId: string;
  search: string;
  onClose: () => void;
}) {
  const [channel, setChannel] = useState<NudgeChannel>("email");
  const [copied, setCopied] = useState<"idle" | "done" | "failed">("idle");

  // The draft is fetched once and does NOT refresh on live events: a message
  // being retyped under the user's cursor because a record changed elsewhere
  // would be worse than a draft that is a few seconds stale.
  const { data, failure } = useResource<NudgeResponse>(
    `nudge:${itemId}${search}`,
    () => fetchNudge(itemId, search),
    0,
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  const draft = data ? (channel === "email" ? data.nudge.email : data.nudge.text) : null;

  /** What lands on the clipboard. The subject travels WITH the email — a
   *  pasted body with no subject line is half a message. */
  const clipboardText = draft
    ? draft.subject
      ? `Subject: ${draft.subject}\n\n${draft.body}`
      : draft.body
    : "";

  return (
    <div
      data-testid="nudge-sheet"
      onClick={onClose}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 28,
        background: C.scrim,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 470,
          maxHeight: "100%",
          overflow: "auto",
          background: C.surface,
          borderRadius: 20,
          border: `1px solid ${C.line}`,
          boxShadow: `0 30px 70px -20px ${C.line2}`,
        }}
        className="sb"
      >
        {failure && (
          <FailurePanel failure={failure as ApiError} />
        )}

        {!failure && !data && (
          <div style={{ ...META, padding: 28, color: C.sub2 }}>Composing…</div>
        )}

        {data && draft && (
          <>
            {/* ---------- header ---------- */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "18px 20px 15px",
                borderBottom: `1px solid ${C.line}`,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{ display: "block", fontSize: 18.4, fontWeight: 700, letterSpacing: "-0.3px" }}
                  data-testid="nudge-title"
                >
                  {data.nudge.recipient.displayName
                    ? `Nudge ${data.nudge.recipient.displayName}`
                    : "Message"}
                </span>
                <span
                  style={{ ...META, display: "block", marginTop: 3, fontSize: 10.93, color: C.sub }}
                >
                  {data.nudge.recipient.raw ?? "No recipient recorded"}
                </span>
              </span>
              <button
                onClick={onClose}
                aria-label="Close"
                data-testid="nudge-close"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  background: C.faint,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke={C.sub}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div style={{ padding: "17px 20px 20px" }}>
              {/* ---------- what this is about ---------- */}
              <div style={{ ...ROW_LINE, gap: 8, marginBottom: 12 }}>
                <KindChip kind={data.item.kind} />
                <Pill {...STATUS_CHIP[data.item.status]} />
                <span
                  data-testid="row-title"
                  style={{ ...ROW_TITLE, fontSize: 14.95, fontWeight: 600 }}
                >
                  {data.item.title}
                </span>
              </div>

              {/* ---------- the honest note about the recipient ---------- */}
              <div
                data-testid="nudge-note"
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: draft.needsRecipientLookup ? C.amberSoft : C.blueSoft,
                  color: C.sub,
                  fontSize: 14.37,
                  lineHeight: 1.45,
                  marginBottom: 14,
                }}
              >
                {channel === "text"
                  ? "LedAI holds no phone number for anybody — you will need to supply it. Copy the draft and send it from your phone."
                  : draft.to
                    ? `LedAI drafts it — you send it from your own email. It goes to ${draft.to}.`
                    : `LedAI has no email address for ${
                        data.nudge.recipient.displayName ?? "this party"
                      } — only a name from the record. Copy the draft and address it yourself.`}
              </div>

              {/* ---------- channel ---------- */}
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }} data-testid="nudge-channels">
                {CHANNELS.map((entry) => {
                  const active = entry.id === channel;
                  return (
                    <button
                      key={entry.id}
                      onClick={() => {
                        setChannel(entry.id);
                        setCopied("idle");
                      }}
                      data-testid={`nudge-channel-${entry.id}`}
                      aria-pressed={active}
                      style={{
                        height: 30,
                        padding: "0 13px",
                        borderRadius: 9,
                        background: active ? C.faint : C.surface,
                        border: `1px solid ${active ? C.line2 : C.line}`,
                        fontSize: 14.37,
                        fontWeight: active ? 700 : 500,
                      }}
                    >
                      {entry.tab}
                    </button>
                  );
                })}
              </div>

              <div style={{ ...META, fontSize: 10.35, letterSpacing: "1px", color: C.sub2, marginBottom: 6 }}>
                {CHANNELS.find((entry) => entry.id === channel)!.label}
              </div>

              {/* ---------- the draft ---------- */}
              <div
                data-testid="nudge-draft"
                style={{
                  background: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: 13,
                  padding: "14px 15px",
                  marginBottom: 16,
                }}
              >
                {draft.subject && (
                  <div
                    style={{
                      fontSize: 15.52,
                      fontWeight: 700,
                      marginBottom: 8,
                      paddingBottom: 8,
                      borderBottom: `1px solid ${C.line}`,
                    }}
                  >
                    {draft.subject}
                  </div>
                )}
                <div style={{ fontSize: 16.1, lineHeight: 1.55, whiteSpace: "pre-line" }}>
                  {draft.body}
                </div>
              </div>

              {/* ---------- copy. not send. ---------- */}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={onClose}
                  style={{
                    height: 46,
                    padding: "0 18px",
                    borderRadius: 12,
                    background: C.surface,
                    border: `1px solid ${C.line2}`,
                    fontSize: 15.52,
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  data-testid="nudge-copy"
                  onClick={async () => {
                    setCopied((await copyToClipboard(clipboardText)) ? "done" : "failed");
                  }}
                  style={{
                    flex: 1,
                    height: 46,
                    borderRadius: 12,
                    background: C.gold,
                    color: C.onGold,
                    fontSize: 16.1,
                    fontWeight: 700,
                  }}
                >
                  {copied === "done"
                    ? "Copied"
                    : copied === "failed"
                      ? "Could not copy — select it and copy by hand"
                      : "Copy the draft"}
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 7,
                  marginTop: 12,
                  fontSize: 13.23,
                  color: C.sub2,
                  lineHeight: 1.45,
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ flexShrink: 0, marginTop: 2 }}
                >
                  <rect x="3" y="7" width="10" height="7" rx="1.6" stroke={C.sub2} strokeWidth="1.7" />
                  <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke={C.sub2} strokeWidth="1.7" />
                </svg>
                <span data-testid="nudge-no-send">
                  LedAI does not send this. Nothing leaves here, and nothing on the item changes
                  until you actually send it — so the stall clock keeps telling the truth.
                </span>
              </div>

              {data.nudge.locationLabel && (
                <div
                  style={{
                    ...META,
                    marginTop: 10,
                    fontSize: 10.35,
                    fontWeight: 400,
                    color: C.sub2,
                    fontFamily: FONT_MONO,
                  }}
                  data-testid="nudge-location"
                >
                  {`Anchored to ${data.nudge.locationLabel}`}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
